import React, { useState, useEffect, useCallback, useContext } from "react";

import { useTranslation } from "react-i18next";
import { usePage } from "../../routes";
import { useAssignMultipleWorkOrders } from "../data/Assignment/useAssignMultipleWorkorders";
import { useReassignMultipleWorkOrders } from "../data/Assignment/useReassignMultipleWorkorders";

import { DateTime } from "luxon";
import {
  DataTable,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { MultiSelect } from "primereact/multiselect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSave } from "@fortawesome/free-solid-svg-icons";
import {
  WorkOrderAssignmentSubmission,
  WorkOrderWithAssignment,
  WorkOrderWithAssignStatus,
} from "../../components/shared/interfaces/WorkOrder";
import { Statuses, StatusesIn360 } from "../../components/shared/Enums";
import { TextTemplate } from "../../components/shared/Grid/BodyTemplates";
import { GlobalFilter } from "../../components/shared/Grid/GlobalFilterTemplate";
import { StatusTag } from "../../components/StatusTag";
import { Link } from "react-router-dom";
import { WorkorderViewer } from "./WorkorderViewer";
import { UserDropdown } from "../UserDropdown";
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox";
import { Snackbar } from "../shared/Snackbar";
import { ConfirmationDialog } from "../shared/ConfirmationDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Card, Grid, Stack, Button } from "@mui/material";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import CreateWorkorderButton from "./CreateWorkorderButton";

import { AccessControlContext } from "../contexts/AccessControlContext";

import "../../components/shared/Grid/gridStyles.css";
import { CancelButton } from "../shared/CancelButton";
import { LOCALSTORAGE_KEYS } from "../shared/constants";
import { DateFilter } from "../shared/DateFilter";

interface Props {
  workorderData?: WorkOrderWithAssignment[];
  workorderDataLoading: boolean;
  workorderDataFetching: boolean;
  onDataRefreshClick: () => void;
  onNewWorkorderClick: () => void;
  onBulkWorkorderCreateClick: () => void;
  selectedCustomerId: number | undefined;
  companyId: number | undefined;
  siteId: number | undefined;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.workOrdersSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.workOrdersSortOrder;

export function WorkorderGrid(props: Props) {
  const { t } = useTranslation();
  const workorderViewerPath = usePage(WorkorderViewer.name)?.path;

  const accessControlContext = useContext(AccessControlContext);
  const userCanEditWorkorders = accessControlContext.canI("edit", "workorder", props.selectedCustomerId);
  const userCanAddWorkorders = accessControlContext.canI("add", "workorder", props.selectedCustomerId);
  const userCanEditAssignments = accessControlContext.canI("edit", "assignment", props.selectedCustomerId);
  const userCanAddAssignments = accessControlContext.canI("add", "assignment", props.selectedCustomerId);

  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [gridData, setGridData] = useState<WorkOrderWithAssignStatus[] | undefined>(
    props.workorderData?.map((workorder) => ({ ...workorder, assign: false })),
  );

  const [bulkEditModeEnabled, setBulkEditModeEnabled] = useState(false);
  const [deviceUserId, setDeviceUserId] = useState<string | undefined>(undefined);
  const [username, setUsername] = useState<string | undefined>(undefined);

  const [assignMultipleWorkordersError, setAssignMultipleWorkordersError] = useState(false);
  const [reassignMultipleWorkordersError, setReassignMultipleWorkordersError] = useState(false);

  const [confirmReassignment, setConfirmReassignment] = useState(false);
  const [cofirmationMessage, setConfirmationMessage] = useState("");

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const {
    mutate: assignMultipleWorkorders,
    isError: errorAssigningMultipleWorkorders,
    isLoading: multipleWorkorderAssignmentsLoading,
    isSuccess: multipleWorkordersSuccessfullyAssigned,
    reset: resetAssignMultipleWorkorders,
  } = useAssignMultipleWorkOrders();

  const {
    mutate: reassignMultipleWorkorders,
    isError: errorReassigningMultipleWorkorders,
    isLoading: multipleWorkorderReassignmentsLoading,
    isSuccess: multipleWorkordersSuccessfullyReassigned,
    reset: resetReassignMultipleWorkorders,
  } = useReassignMultipleWorkOrders();

  const onWorkordersUpdated = useCallback(() => {
    setSnackbarText(t("workorders.workordersUpdated"));
    setShowSnackbar(true);
  }, [t]);

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = localStorage.getItem(sortFieldStorageKey) as string;
    const order = localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  useEffect(() => {
    setAssignMultipleWorkordersError(errorAssigningMultipleWorkorders);
  }, [errorAssigningMultipleWorkorders]);

  useEffect(() => {
    setReassignMultipleWorkordersError(errorReassigningMultipleWorkorders);
  }, [errorReassigningMultipleWorkorders]);

  useEffect(() => {
    if (
      !multipleWorkorderAssignmentsLoading &&
      !multipleWorkorderReassignmentsLoading &&
      (multipleWorkordersSuccessfullyReassigned || multipleWorkordersSuccessfullyAssigned) &&
      !errorReassigningMultipleWorkorders &&
      !errorAssigningMultipleWorkorders &&
      bulkEditModeEnabled &&
      deviceUserId &&
      !confirmReassignment
    ) {
      onWorkordersUpdated();
      setBulkEditModeEnabled(false);
      setDeviceUserId(undefined);
      resetAssignMultipleWorkorders();
      resetReassignMultipleWorkorders();
      props.onDataRefreshClick();
    }
  }, [
    bulkEditModeEnabled,
    confirmReassignment,
    deviceUserId,
    errorAssigningMultipleWorkorders,
    errorReassigningMultipleWorkorders,
    multipleWorkorderAssignmentsLoading,
    multipleWorkorderReassignmentsLoading,
    multipleWorkordersSuccessfullyAssigned,
    multipleWorkordersSuccessfullyReassigned,
    onWorkordersUpdated,
    props,
    resetAssignMultipleWorkorders,
    resetReassignMultipleWorkorders,
  ]);

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;

    setGlobalFilter(element.value);
  }

  function handleAssignedUserChange(deviceUserId: string) {
    setDeviceUserId(deviceUserId);
  }

  function onAssign() {
    if (!assignmentIsValid()) {
      setSnackbarText(t("workorders.assignmentError"));
      setShowSnackbar(true);
    } else {
      const reassignments = gridData?.filter((row) => row.assign === true && row.assignment);
      prepareReassignments(reassignments);
      if (reassignments == null || reassignments.length === 0) {
        const assignments = gridData?.filter((row) => row.assign === true && row.assignment == null);
        prepareAssignments(assignments);
      }
    }
  }

  const assignPreviouslyUnassignedWorkorders = useCallback(
    (workordersToAssign: WorkOrderWithAssignStatus[]) => {
      if (deviceUserId) {
        const workorderAssignmentSubmissions = workordersToAssign.map((workorderToAssign) => {
          const workorderAssignmentSubmission: WorkOrderAssignmentSubmission = {
            workOrderId: workorderToAssign.id,
            deviceUserId: deviceUserId,
          };
          return workorderAssignmentSubmission;
        });
        assignMultipleWorkorders(workorderAssignmentSubmissions);
      }
    },
    [assignMultipleWorkorders, deviceUserId],
  );

  const prepareReassignments = useCallback(
    (workOrdersToReassign: WorkOrderWithAssignStatus[] | undefined) => {
      if (workOrdersToReassign !== undefined && workOrdersToReassign.length !== 0 && deviceUserId) {
        setConfirmReassignment(true);
        const workOrders = workOrdersToReassign
          .map((workOrderToReassign) => `${workOrderToReassign.id} - ${workOrderToReassign.assignment?.userName}`)
          .toString();
        setConfirmationMessage(
          t("workorders.reassignWarning", {
            workorders: workOrders,
            user: username,
          }),
        );
      }
    },
    [deviceUserId, t, username],
  );

  const prepareAssignments = useCallback(
    (workOrdersToAssign: WorkOrderWithAssignStatus[] | undefined) => {
      if (workOrdersToAssign !== undefined && workOrdersToAssign.length !== 0 && deviceUserId) {
        assignPreviouslyUnassignedWorkorders(workOrdersToAssign);
      }
    },
    [assignPreviouslyUnassignedWorkorders, deviceUserId],
  );

  const reassignPreviouslyAssignedWorkorders = useCallback(() => {
    const workordersToReassign = gridData?.filter((row) => row.assign === true && row.assignment);
    if (workordersToReassign != null && workordersToReassign && deviceUserId) {
      const workorderReassignmentSubmissions = workordersToReassign.map((workorderToReassign) => {
        const workorderReassignmentSubmission: WorkOrderAssignmentSubmission = {
          workOrderId: workorderToReassign.id,
          deviceUserId: deviceUserId,
        };
        return workorderReassignmentSubmission;
      });
      reassignMultipleWorkorders(workorderReassignmentSubmissions);
      prepareAssignments(gridData?.filter((row) => row.assign === true && row.assignment == null));
    }
  }, [deviceUserId, gridData, prepareAssignments, reassignMultipleWorkorders]);

  function assignmentIsValid(): boolean {
    const workOrdersToAssign = gridData?.filter((row) => row.assign === true);
    return deviceUserId != null && workOrdersToAssign != null && workOrdersToAssign.length !== 0;
  }

  function exitEditMode() {
    setDeviceUserId(undefined);
    setBulkEditModeEnabled(false);
    setGridData(gridData && gridData.map((row) => ({ ...row, assign: false })));
  }

  function handleCheckboxChange(rowId: number) {
    return (event: CheckboxChangeParams) => {
      const isChecked = event.target.checked;
      setGridData(
        gridData &&
          gridData.map((row) => {
            if (row.id === rowId) return { ...row, assign: isChecked };
            return row;
          }),
      );
    };
  }

  useEffect(() => {
    setGridData(
      props.workorderData?.map((workorder) => ({
        ...workorder,
        assign: false,
      })),
    );
  }, [props.workorderData]);

  function cancelReassignment() {
    setConfirmReassignment(false);
  }

  function handleReassignmentConfirmation() {
    reassignPreviouslyAssignedWorkorders();
  }

  const headerToolbar = (
    <Grid container alignItems="center">
      <Grid item>
        <Stack direction="row" spacing={2} alignItems="center">
          {userCanAddWorkorders && (
            <CreateWorkorderButton
              onCreateNewWorkOrderClick={props.onNewWorkorderClick}
              onCreateMultiWorkOrderClick={props.onBulkWorkorderCreateClick}
            />
          )}

          {userCanEditWorkorders && userCanEditAssignments && userCanAddAssignments && bulkEditModeEnabled ? (
            <Card>
              <Stack direction="row" spacing={2} margin={2}>
                <UserDropdown
                  name={"userId"}
                  selectedDeviceUserId={deviceUserId}
                  customerId={props.selectedCustomerId}
                  onAssignedUserChange={handleAssignedUserChange}
                  onUsernameChange={(username) => setUsername(username)}
                />
                <Button variant="contained" onClick={onAssign} startIcon={<FontAwesomeIcon icon={faSave} />}>
                  {t("workorders.assignUser")}
                </Button>
                <CancelButton onClick={exitEditMode} />
              </Stack>
            </Card>
          ) : (
            userCanEditWorkorders &&
            userCanEditAssignments &&
            userCanAddAssignments && (
              <Button
                variant="contained"
                onClick={() => setBulkEditModeEnabled(true)}
                startIcon={<FontAwesomeIcon icon={faEdit} />}
              >
                {t("workorders.editAssignments")}
              </Button>
            )
          )}
        </Stack>
      </Grid>
      <Grid item xs display="flex" justifyContent="center">
        <GlobalFilter onInput={handleGlobalFilterInput} />
      </Grid>
      <Grid item xs display="flex" justifyContent="flex-end">
        <RefreshLoadingButton isLoading={props.workorderDataFetching} onClick={props.onDataRefreshClick} />
      </Grid>
    </Grid>
  );

  const actionBodyTemplate = (rowData: WorkOrderWithAssignment, column: any) => {
    return (
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to={{
          pathname: workorderViewerPath
            ?.replace(":workorderId", rowData.id.toString())
            .replace(":customerId", props.selectedCustomerId?.toString() ?? "0")
            .replace(":companyId", props.companyId?.toString() ?? "0")
            .replace(":siteId", props.siteId?.toString() ?? "0"),
        }}
      >
        <VisibilityIcon />
      </Button>
    );
  };

  const statusItemTemplate = (option: string) => {
    return <StatusTag status={option} label={option} />;
  };

  const statusFilter = (options: ColumnFilterElementTemplateOptions) => (
    <MultiSelect
      value={options.value}
      options={Object.values(StatusesIn360)}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t("common.filter", { val: "$t(common.status)" })}
      showClear
      itemTemplate={statusItemTemplate}
    />
  );

  const applyDateFilter = (value: any, filter: any) => {
    if (!filter) return true;

    const rowDate = DateTime.fromISO(value).startOf("day");
    const filterDate = DateTime.fromJSDate(filter).toUTC().startOf("day");

    return filterDate.hasSame(rowDate, "day");
  };

  const dateFilter = (options: ColumnFilterElementTemplateOptions) => <DateFilter options={options} />;

  if (props.workorderDataLoading || multipleWorkorderAssignmentsLoading || multipleWorkorderReassignmentsLoading)
    return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            value={gridData}
            header={headerToolbar}
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("common.emptyMessage", {
              val: "$t(workorders.workorders )",
            })}
            loading={props.workorderDataLoading}
            multiSortMeta={multiSortMeta}
            onSort={onSort}
          >
            {bulkEditModeEnabled && (
              <Column
                field={"assign"}
                header={t("workorders.assign")}
                body={(rowData: WorkOrderWithAssignStatus) => (
                  <React.Fragment>
                    <span className="w-100 d-flex justify-content-center">
                      {(rowData.status === Statuses.ASSIGNED || rowData.status === Statuses.READY) && (
                        <Checkbox
                          key={`checkbox-${rowData.id}`}
                          id={rowData.id.toString()}
                          checked={rowData.assign}
                          onChange={handleCheckboxChange(rowData.id)}
                        />
                      )}
                    </span>
                  </React.Fragment>
                )}
                sortable
              />
            )}
            <Column
              field={"id"}
              header={t("workorders.workorderNumber")}
              body={(rowData: WorkOrderWithAssignStatus) => (
                <TextTemplate rowData={rowData} field={"id"} header={t("workorders.workorderNumber")} />
              )}
              sortable
              sortField="id"
            />
            <Column
              field={"substationName"}
              header={t("substations.substation")}
              body={(rowData: WorkOrderWithAssignStatus) => (
                <TextTemplate rowData={rowData} field={"substationName"} header={t("substations.substation")} />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="substationName"
            />
            <Column
              field={"subjectAssetName"}
              header={t("asset.assets")}
              body={(rowData: WorkOrderWithAssignStatus) => (
                <TextTemplate rowData={rowData} field={"subjectAssetName"} header={t("asset.assets")} />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="subjectAssetName"
            />
            <Column
              field={"targetStartDate"}
              header={t("common.targetStartDate")}
              body={(rowData: WorkOrderWithAssignStatus) =>
                rowData.targetStartDate !== null
                  ? t("common.formattedDateShort", {
                      dateString: rowData.targetStartDate,
                    })
                  : undefined
              }
              filter
              filterMatchMode={FilterMatchMode.CUSTOM}
              filterElement={dateFilter}
              filterFunction={applyDateFilter}
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="targetStartDate"
            />

            <Column
              field={"targetCompletionDate"}
              header={t("common.targetCompletionDate")}
              body={(rowData: WorkOrderWithAssignStatus) =>
                rowData.targetCompletionDate !== null
                  ? t("common.formattedDateShort", {
                      dateString: rowData.targetCompletionDate,
                    })
                  : undefined
              }
              filter
              filterMatchMode={FilterMatchMode.CUSTOM}
              filterElement={dateFilter}
              filterFunction={applyDateFilter}
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="targetCompletionDate"
            />
            <Column
              field={"status"}
              header={t("common.status")}
              body={(rowData: WorkOrderWithAssignStatus) => (
                <React.Fragment>
                  <span className="p-column-title">{t("common.status")}</span>
                  <span className="w-100 d-flex justify-content-center">
                    <StatusTag
                      status={rowData["status"]}
                      label={t(`workorders.statuses.${rowData["status"].toLowerCase()}`)}
                    />
                  </span>
                </React.Fragment>
              )}
              filter
              filterMatchMode={FilterMatchMode.IN}
              sortable
              showFilterMenu={false}
              showClearButton={false}
              filterElement={statusFilter}
              sortField="status"
            />
            <Column
              field={"assignment.userName"}
              header={t("workorders.assignedTo")}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="assignment.userName"
            />
            <Column
              body={actionBodyTemplate}
              headerStyle={{ width: "8em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center", overflow: "visible" }}
              header={t("common.actions")}
            />
          </DataTable>
        </div>

        {confirmReassignment && (
          <ConfirmationDialog
            message={cofirmationMessage}
            onAccept={handleReassignmentConfirmation}
            onClose={cancelReassignment}
            visible={confirmReassignment}
            header={t("workorders.reassign")}
            closeMessage={t("common.cancel")}
          />
        )}

        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />

        <Snackbar
          open={assignMultipleWorkordersError || reassignMultipleWorkordersError}
          onClose={() => {
            setAssignMultipleWorkordersError(false);
            setReassignMultipleWorkordersError(false);
          }}
          message={t("workorders.errorAssigningWorkorders")}
        />
      </React.Fragment>
    );
}
